import { html } from 'lit-html';
import { get } from '../api.js';
import { app, isOwner, type Page } from '../app.js';
import { pill } from '../format.js';
import type { Connector } from '../types.js';

interface Overview {
  stage: string;
  force_test_mode: boolean;
  keys: { total: number; active: number; revoked: number; expired: number };
  connectors: Connector[];
}
let data: Overview | null = null;

const STATE_LABEL: Record<string, string> = { queued: 'Held', sending: 'Sending', submitted: 'Sent', failed: 'Failed', cancelled: 'Cancelled' };

export const overview: Page = {
  title: 'Overview',
  async load() {
    data = await get<Overview>('/overview');
  },
  view() {
    if (!data) return html``;
    const stannp = data.connectors.find((c) => c.name === 'stannp');
    const keyConfigured = stannp?.secrets.find((s) => s.name === 'api_key')?.configured ?? false;
    const hookConfigured = stannp?.secrets.find((s) => s.name === 'webhook_secret')?.configured ?? false;
    const recent = (stannp?.summary?.recent_mail ?? {}) as Record<string, number>;
    const steps = [
      { done: keyConfigured, text: html`Set the <b>Stannp API key</b> on the <a href="#/connectors/stannp">Stannp page</a>${isOwner() ? '' : ' (owners only)'}.` },
      { done: hookConfigured, text: html`Set a <b>webhook secret</b> there, then add the webhook URL in Stannp with the same secret, so delivery status comes back.` },
      { done: Boolean(stannp?.enabled), text: html`Switch the Stannp connector <b>on</b>.` },
      { done: data.keys.active > 0, text: html`Create an <a href="#/keys">API key</a> for MoEngage (start with a <b>test</b> key).` },
      { done: false, text: html`On a <a href="#/connectors/stannp/templates">template</a>, use <b>Send a test</b> and check the PDF, then copy the sample request into MoEngage.` },
    ];
    return html`
      <h1>Overview</h1>
      <p class="hint">connectors.catch-tools.com gives internal systems one keyed address for outside services. Each connector lives at
        <code>${app.me.public_url}/{connector}</code>; this console manages who may call what.</p>
      ${data.force_test_mode ? html`<div class="banner info"><b>Test environment.</b> Every send here runs in the vendor's test mode: Stannp renders a PDF but prints and posts nothing.</div>` : ''}
      <div class="grid kpis">
        <div class="kpi"><span class="label">Active API keys</span><span class="value">${data.keys.active}</span><span class="sub">${data.keys.revoked} revoked · ${data.keys.expired} expired</span></div>
        <div class="kpi"><span class="label">Connectors on</span><span class="value">${data.connectors.filter((c) => c.enabled).length} / ${data.connectors.length}</span></div>
        ${Object.entries(STATE_LABEL).filter(([s]) => recent[s]).map(([s, label]) => html`<div class="kpi ${s === 'failed' ? 'bad' : ''}"><span class="label">Mail · ${label}</span><span class="value">${recent[s]}</span><span class="sub">of the latest ${String(stannp?.summary?.recent_sample ?? 0)}</span></div>`)}
      </div>
      <section class="panel"><h2>Connectors</h2>
        <div class="grid two">${data.connectors.map((c) => html`<a class="card link-card" href="#/connectors/${c.name}">
          <div class="row-between"><h2>${c.display_name}</h2>${c.enabled ? pill('On', 'positive') : pill('Off')}</div>
          <p class="muted">${c.description}</p>
          <p><code>${c.url}</code></p>
          ${c.secrets.filter((s) => s.required && !s.configured).map((s) => html`<p>${pill(`${s.label} not set`, 'warning')}</p>`)}
        </a>`)}</div>
      </section>
      ${stannp && steps.some((s) => !s.done)
        ? html`<section class="panel"><h2>Getting the welcome letters live</h2>
            <ol class="steps">${steps.map((s) => html`<li>${s.done ? pill('Done', 'positive') : ''} ${s.text}</li>`)}</ol>
            <p class="hint">The full checklist, including switching off the CRM's old letter queue, is in docs/MOENGAGE-SETUP.md.</p>
          </section>`
        : ''}`;
  },
};
